import { useSubpantalla } from '../ui/navegacionReact';
import { useCallback, useEffect, useState } from 'react';
import { ArrowDownUp, MapPin, ShoppingCart } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import ListaEstandar from '../ui/ListaEstandar';
import EstadoVacio from '../ui/EstadoVacio';
import NotaExplicativa from '../ui/NotaExplicativa';
import Ayuda from '../ui/Ayuda';
import ModalConfirmar from '../ui/ModalConfirmar';
import { useAvisos } from '../lib/contextoAvisos';

// Reponer el hueco de surtido antes de que se vacíe.
//
// QUÉ ES, Y QUÉ NO ES. El punto de reorden dice cuándo pedirle al
// proveedor. Esto dice cuándo MOVER lo que ya se tiene: de la reserva
// —donde está la tarima entera— al hueco del que se surte todos los días.
// Un almacén puede tener cuatrocientas piezas y quedarse sin vender porque
// las cuatro que caben en la vitrina se acabaron el martes.
//
// EL ORDEN ES EL DEL ÁRBOL QUE DECLARÓ EL CLIENTE, y la pantalla lo dice
// con esas palabras. No es una ruta optimizada: si los pasillos están
// nombrados en el orden en que se caminan, el recorrido sale bien; si
// están nombrados al azar, sale igual de al azar. Prometer lo otro sería
// hacer que alguien confíe en un orden inventado.

const num = (v) => {
  const d = Number(v || 0);
  return d === Math.trunc(d) ? String(Math.trunc(d)) : d.toFixed(3).replace(/0+$/, '');
};

export default function Reabastecimiento() {
  const [pestana, setPestana] = useState('recorrido');
  useSubpantalla('pestana', pestana !== 'recorrido', () => setPestana('recorrido'), { valor: pestana, abrir: setPestana, enUrl: true, reemplaza: true });
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Reabastecimiento</h2>
        <p className="text-sm text-gray-500 mt-0.5">
          Mover lo que ya tienes al hueco del que se despacha, antes de que se vacíe.
        </p>
      </div>

      <div className="border-b border-gray-200 flex gap-1 flex-wrap">
        {[['recorrido', 'Qué reponer'], ['puntos', 'Puntos de surtido']].map(([id, et]) => (
          <button key={id} type="button" onClick={() => setPestana(id)} aria-current={pestana === id ? 'page' : undefined}
                  className={`px-3 py-2 text-sm font-semibold border-b-2 -mb-px ${
                    pestana === id
                      ? 'border-[var(--color-marca)] text-[var(--color-marca)]'
                      : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
            {et}
          </button>
        ))}
      </div>

      {pestana === 'recorrido' ? <Recorrido /> : <Puntos />}
    </div>
  );
}

// ------------------------------------------------------------ recorrido
function Recorrido() {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [mover, setMover] = useState(null);

  const cargar = useCallback(
    () => api.get('/reabastecimiento')
      .then((d) => { setDatos(d); setError(null); })
      .catch((e) => setError(e.message)),
    [],
  );

  useEffect(() => {
    let vivo = true;
    api.get('/reabastecimiento')
      .then((d) => vivo && setDatos(d))
      .catch((e) => vivo && setError(e.message));
    return () => { vivo = false; };
  }, []);

  if (error) return <p className="text-sm text-red-700">{error}</p>;
  if (!datos) return <p className="text-sm text-gray-500">Mirando qué falta…</p>;

  return (
    <div className="space-y-4">
      <NotaExplicativa titulo="En qué orden está esta lista, y en cuál no">
        Va en el orden del <strong>árbol de ubicaciones que diste de alta</strong>,
        por el sitio del que hay que recoger cada cosa. Si nombraste tus pasillos
        en el orden en que se caminan, este es el recorrido; si no,{' '}
        <strong>no es una ruta optimizada</strong> y el sistema no puede saber cuál
        de las dos cosas hiciste.
      </NotaExplicativa>

      {datos.tareas.length === 0 && (datos.sin_existencia || []).length === 0 && (
        <EstadoVacio
          icono={ArrowDownUp}
          titulo="No hay nada que reponer"
          descripcion="Todos los huecos de surtido están por encima de su mínimo. Aquí aparecerán en cuanto alguno baje."
        />
      )}

      {datos.tareas.length > 0 && (
        <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 bg-white">
          {datos.tareas.map((t) => (
            <div key={`${t.sku}:${t.ubicacion}`} className="p-3 flex items-start gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-gray-900">{t.producto}</div>
                <div className="text-xs font-mono text-gray-500">{t.sku}</div>

                {/* De dónde sacarlo, y cuánto de cada sitio. Decir «trae 40»
                    cuando en la reserva hay 12 obliga a rehacer la cuenta
                    en el pasillo. */}
                <ul className="mt-1.5 space-y-0.5">
                  {t.origenes.map((o) => (
                    <li key={o.ubicacion} className="text-sm text-gray-700 flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5 text-gray-400 shrink-0" aria-hidden="true" />
                      Trae <strong className="tabular-nums">{num(o.cantidad)}</strong> de{' '}
                      <span className="font-mono text-xs">{o.ubicacion}</span>
                      {o.caduca && (
                        <span className="text-xs text-amber-700">· caduca {o.caduca}</span>
                      )}
                    </li>
                  ))}
                </ul>

                {/* Lo que no alcanza se dice aquí, no se calla: mandar a por
                    20 cuando solo hay 12 gasta el viaje y la confianza. */}
                {Number(t.falta_por_comprar) > 0 && (
                  <p className="text-xs text-amber-700 mt-1 font-semibold">
                    Con esto llegas a {num(Number(t.hay) + Number(t.se_puede_mover))} de {num(t.maximo)}.
                    Faltan {num(t.falta_por_comprar)} que no hay en ninguna otra
                    ubicación: eso es una compra.
                  </p>
                )}
              </div>

              <div className="text-right shrink-0">
                <div className="text-xs text-gray-500">Llevar a</div>
                <div className="font-mono text-sm text-gray-900">{t.ubicacion}</div>
                <div className="text-xs text-gray-500 mt-0.5 tabular-nums">
                  hay {num(t.hay)} · mínimo {num(t.minimo)}
                </div>
                <button
                  type="button" onClick={() => setMover(t)}
                  className="mt-2 inline-flex items-center gap-1.5 bg-[var(--color-marca)] hover:bg-[var(--color-marca-fuerte)]
                             text-white text-sm font-semibold px-3 py-1.5 rounded-lg
                             focus:outline-none focus:ring-2 focus:ring-[var(--color-marca)] focus:ring-offset-1"
                >
                  <ArrowDownUp className="h-4 w-4" aria-hidden="true" /> Reponer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* NO ES TRABAJO DE ALMACÉN, y por eso va aparte y no como tarea:
          no hay de dónde sacarlo, así que caminar no lo arregla. */}
      {(datos.sin_existencia || []).length > 0 && (
        <div className="border border-amber-200 bg-amber-50 rounded-xl p-3">
          <h3 className="font-semibold text-amber-900 text-sm flex items-center gap-1.5">
            <ShoppingCart className="h-4 w-4" aria-hidden="true" />
            Esto no se arregla moviendo: hay que comprarlo
          </h3>
          <p className="text-xs text-amber-800 mt-0.5 mb-2">
            El hueco está bajo mínimo y no hay existencia en ninguna otra
            ubicación disponible. Recorrer el almacén no lo va a resolver.
          </p>
          <ul className="space-y-1">
            {(datos.sin_existencia || []).map((t) => (
              <li key={`${t.sku}:${t.ubicacion}`} className="text-sm text-amber-900">
                <span className="font-mono text-xs">{t.sku}</span> · {t.producto} —
                faltan <strong className="tabular-nums">{num(t.reponer)}</strong> en{' '}
                <span className="font-mono text-xs">{t.ubicacion}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {datos.truncado && (
        <p className="text-xs text-gray-500">
          Se muestran los primeros {datos.tareas.length + (datos.sin_existencia || []).length} de{' '}
          {datos.total} huecos bajo mínimo.
        </p>
      )}

      {mover && (
        <ModalReponer tarea={mover} onCerrar={() => setMover(null)}
                      onListo={() => { setMover(null); cargar(); }} />
      )}
    </div>
  );
}

// EL TRASPASO ES EL DE SIEMPRE. Reponer no es un movimiento nuevo: es un
// traspaso con los datos ya puestos. Inventar un tipo propio dejaría el
// kárdex con dos formas de contar lo mismo.
function ModalReponer({ tarea, onCerrar, onListo }) {
  const avisos = useAvisos();
  const [origen, setOrigen] = useState(tarea.origenes[0]?.ubicacion || '');
  const sugerida = tarea.origenes.find((o) => o.ubicacion === origen)?.cantidad;
  const [cantidad, setCantidad] = useState(String(num(sugerida ?? 0)));

  function cambiarOrigen(codigo) {
    setOrigen(codigo);
    // La cantidad sigue al origen: dejar puesto lo del sitio anterior
    // haría pedir 30 de un hueco que tiene 8.
    const o = tarea.origenes.find((x) => x.ubicacion === codigo);
    setCantidad(String(num(o?.cantidad ?? 0)));
  }

  return (
    <ModalConfirmar
      titulo={`Reponer ${tarea.sku}`}
      descripcion="Se registra como un traspaso normal: sale del origen y entra al hueco de surtido, con su asiento en el kárdex."
      extra={
        <div className="space-y-3">
          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">
              De dónde
            </span>
            <select value={origen} onChange={(e) => cambiarOrigen(e.target.value)}
                    className="entrada">
              {tarea.origenes.map((o) => (
                <option key={o.ubicacion} value={o.ubicacion}>
                  {o.ubicacion} — hay {num(o.cantidad)} disponible(s)
                  {o.caduca ? ` · caduca ${o.caduca}` : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">
              Cuánto llevar a {tarea.ubicacion}
            </span>
            <input type="number" min="0" step="any" value={cantidad}
                   onChange={(e) => setCantidad(e.target.value)}
                   className="entrada tabular-nums" />
            <span className="block text-xs text-gray-500 mt-1">
              El hueco admite hasta {num(tarea.maximo)} y ahora tiene {num(tarea.hay)}.
            </span>
          </label>
        </div>
      }
      etiquetaConfirmar="Registrar el traspaso"
      onConfirmar={async () => {
        const n = Number(cantidad);
        if (!(n > 0)) throw new ErrorApi('Indica cuánto vas a llevar.');
        await api.post('/inventario/movimientos', {
          tipo: 'TRASPASO',
          sku: tarea.sku,
          cantidad: n,
          ubicacion_origen: origen,
          ubicacion_destino: tarea.ubicacion,
          motivo: 'Reposición de hueco de surtido',
        });
        avisos.exito(`${num(n)} de ${tarea.sku} movidos a ${tarea.ubicacion}.`);
        onListo();
      }}
      onCerrar={onCerrar}
    />
  );
}

// ---------------------------------------------------- puntos de surtido
function Puntos() {
  const { tieneNivel } = useAuth();
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [nuevo, setNuevo] = useState(false);
  const [retirar, setRetirar] = useState(null);

  const cargar = useCallback(() => {
    setCargando(true);
    return api.get('/reabastecimiento/puntos')
      .then(setFilas)
      .catch((e) => setError(e.message))
      .finally(() => setCargando(false));
  }, []);

  useEffect(() => {
    let vivo = true;
    api.get('/reabastecimiento/puntos')
      .then((d) => vivo && setFilas(d))
      .catch((e) => vivo && setError(e.message))
      .finally(() => vivo && setCargando(false));
    return () => { vivo = false; };
  }, []);

  const columnas = [
    { etiqueta: 'SKU', valor: (p) => p.sku, clase: 'font-mono text-xs' },
    { etiqueta: 'Producto', valor: (p) => p.producto, clase: 'font-medium text-gray-900' },
    { etiqueta: 'Hueco', valor: (p) => p.ubicacion, clase: 'font-mono text-xs' },
    {
      // LO QUE HAY AHORA, para que esto no sea una lista de intenciones:
      // se ve de un vistazo cuáles están por debajo de lo decidido.
      etiqueta: 'Hay', alineado: 'derecha', valor: (p) => Number(p.hay),
      render: (p) => (Number(p.hay) < Number(p.minimo)
        ? <span className="tabular-nums font-semibold text-red-700">{num(p.hay)}</span>
        : <span className="tabular-nums text-gray-700">{num(p.hay)}</span>),
    },
    { etiqueta: 'Mínimo', alineado: 'derecha', valor: (p) => Number(p.minimo),
      render: (p) => <span className="tabular-nums">{num(p.minimo)}</span> },
    { etiqueta: 'Máximo', alineado: 'derecha', valor: (p) => Number(p.maximo),
      render: (p) => <span className="tabular-nums">{num(p.maximo)}</span> },
  ];

  return (
    <>
      <ListaEstandar
        titulo="Puntos de surtido"
        subtitulo="Cuánto tiene que haber siempre de cada producto en cada hueco. Es la decisión que el sistema no puede deducir solo."
        accionPrincipal={tieneNivel('ENCARGADO')
          ? { etiqueta: 'Fijar un hueco', onClick: () => setNuevo(true) } : null}
        columnas={columnas}
        filas={filas}
        cargando={cargando}
        exportar={{ nombre: 'puntos-de-surtido', columnas }}
        vacio={
          <EstadoVacio
            icono={ArrowDownUp}
            titulo="Todavía no hay puntos de surtido"
            descripcion="Empieza por los productos que más se despachan: di en qué hueco viven y cuánto debe haber siempre ahí."
          />
        }
        acciones={(p) => (tieneNivel('ENCARGADO')
          ? [{ etiqueta: 'Retirar', peligro: true, onClick: () => setRetirar(p) }]
          : [])}
      />

      {error && (
        <div className="mt-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          {error}
        </div>
      )}

      {nuevo && (
        <ModalPunto onCerrar={() => setNuevo(false)}
                    onListo={() => { setNuevo(false); cargar(); }} />
      )}

      {retirar && (
        <ModalConfirmar
          titulo={`Retirar el punto de surtido de ${retirar.sku}`}
          descripcion="Deja de aparecer en el recorrido. No se borra ningún movimiento: un punto de surtido es una decisión vigente, no algo que ocurrió."
          etiquetaConfirmar="Retirar"
          peligro
          onConfirmar={async () => {
            await api.del(`/reabastecimiento/puntos/${retirar.id}`);
            setRetirar(null);
            cargar();
          }}
          onCerrar={() => setRetirar(null)}
        />
      )}
    </>
  );
}

function ModalPunto({ onCerrar, onListo }) {
  const [sku, setSku] = useState('');
  const [ubicacion, setUbicacion] = useState('');
  const [minimo, setMinimo] = useState('');
  const [maximo, setMaximo] = useState('');
  const [ubicaciones, setUbicaciones] = useState([]);

  useEffect(() => {
    let vivo = true;
    api.get('/ubicaciones')
      .then((u) => vivo && setUbicaciones(u))
      .catch(() => {});
    return () => { vivo = false; };
  }, []);

  return (
    <ModalConfirmar
      titulo="Fijar un hueco de surtido"
      descripcion="Si ya existía uno para este producto en este hueco, se corrige en vez de duplicarse."
      extra={
        <div className="space-y-3">
          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">SKU</span>
            <input value={sku} onChange={(e) => setSku(e.target.value)}
                   className="entrada font-mono" placeholder="SKU-001" />
          </label>
          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">
              ¿En qué hueco?
            </span>
            <select value={ubicacion} onChange={(e) => setUbicacion(e.target.value)}
                    className="entrada">
              <option value="">— Elige una ubicación —</option>
              {ubicaciones.map((u) => (
                <option key={u.id} value={u.codigo}>{u.codigo} — {u.nombre}</option>
              ))}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">
                Mínimo
              </span>
              <input type="number" min="0" step="any" value={minimo}
                     onChange={(e) => setMinimo(e.target.value)}
                     className="entrada tabular-nums" />
              <Ayuda id="reabastecimiento.minimo" />
            </label>
            <label className="block">
              <span className="block text-sm font-semibold text-gray-700 mb-1">
                Máximo
              </span>
              <input type="number" min="0" step="any" value={maximo}
                     onChange={(e) => setMaximo(e.target.value)}
                     className="entrada tabular-nums" />
              <Ayuda id="reabastecimiento.maximo" />
            </label>
          </div>
        </div>
      }
      etiquetaConfirmar="Guardar"
      onConfirmar={async () => {
        if (!sku.trim()) throw new ErrorApi('Falta el SKU.');
        if (!ubicacion) throw new ErrorApi('Elige el hueco.');
        const mn = Number(minimo);
        const mx = Number(maximo);
        if (!(mn > 0)) throw new ErrorApi('El mínimo tiene que ser mayor que cero.');
        if (!(mx >= mn)) {
          throw new ErrorApi(
            'El máximo no puede ser menor que el mínimo: la misma tarea saldría una y otra vez.',
          );
        }
        await api.post('/reabastecimiento/puntos', {
          sku: sku.trim(), ubicacion, minimo: mn, maximo: mx,
        });
        onListo();
      }}
      onCerrar={onCerrar}
    />
  );
}
